import { CHANGE_LOCATION, YOUR_DETECTED_LOCATION, YOUR_SELECTED_LOCATION, LOCAL_CITY_LOCATION, AVERAGE_RATE_THRESHOLD } from '@constants/constants';
import formatPhoneLink from '@utils/format-phone-link';
import React from 'react';
import { useLocator } from '../../contexts/franchise/locator-provider';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import { RatingSummary } from '../atoms/RatingSummary';
import { borderRadius, breakpoints } from '../primitives/tokens';
import Container from '../atoms/Container';
import { colors } from '../primitives/colors';
import PhoneNumber from '../molecules/phone-number';
import { StyledButtonAsLink } from '../primitives/styled-button';
import Skeleton from '../primitives/skeleton';
import StyledPillButton, { StyledPillButtonAsLink } from '../primitives/styled-pill-button';
import ChangeLocationDrawer from './change-location-drawer';
import Typography from '../primitives/typography';
import LeaveUsAReview from '../atoms/LeaveUsAReview';

const NearestWrap = ({ children, className }: { children: React.ReactNode }): JSX.Element => (
    <Container removeAt="mb" className={className}>
        <div css={styles.nearestWrap}>
            {children}
        </div>
    </Container>
);
/*
* TODO: Remove onChangeLocation prop - this
* should always open the drawer,
* this drawer should be self-contained within nearest
* */

const Nearest = (): JSX.Element => {
    const { hideFranchiseLink } = useCtaProvider();
    const { geo, franchise: locatorFranchise, locationChangedByUser } = useLocator();
    const { isMicrosite, isLocalCityPage, ...microsite } = useMicrosite();

    const [open, setOpen] = React.useState(false);

    const franchise = microsite?.franchise || locatorFranchise?.data;
    const name = franchise?.name;
    const averageRating = franchise?.averageRating;
    const permalink = franchise?.permalink;
    const reviewCount = franchise?.reviewCount;
    const phone = franchise?.mainPhone;
    const reviewLink = microsite?.franchise?.googleReviewLink;
    // determines whether the reviews
    const shouldDisplayReview = reviewCount !== 0 && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD
    // determines whether the entire reviews container (with the rating and leave us review) should appear
    const shouldDisplayReviewContainer = shouldDisplayReview || reviewLink;

    if (isMicrosite) {
        return (
            <NearestWrap css={styles.micrositeNearestOuterWrap(shouldDisplayReviewContainer)}>
                <div css={styles.micrositeNearestInnerWrap}
                >
                    <div css={styles.displayOnDesktop}>
                        <h2 css={styles.nearestHeading}>Here to Help® 24/7.</h2>
                        <div css={styles.nearestLocationWrap}>
                            <h1 css={styles.nearestLocationHeading}>
                                {name}
                            </h1>
                        </div>
                    </div>
                    <div css={styles.reviewsWrap(!!shouldDisplayReviewContainer)}
                    >
                        {shouldDisplayReviewContainer && <div css={styles.ratingWrap}
                        >
                            {shouldDisplayReview && (
                                <div css={styles.rating}
                                >
                                    <Typography
                                        fontSize={{
                                            dt: 'xl',
                                            mb: 'lg',
                                        }}
                                        css={styles.typography}
                                        fontWeight="medium"
                                        color="primaryOrange"
                                    >
                                        Our Google Reviews
                                    </Typography>
                                    <RatingSummary
                                        rating={averageRating}
                                        count={reviewCount}
                                    />
                                </div>
                            )}
                            {reviewLink && (
                                <LeaveUsAReview googleReviewLink={reviewLink} />
                            )}
                        </div>}
                        <div css={styles.micrositePhoneWrapper(!!shouldDisplayReview && !!reviewLink)}
                        >
                            {franchise?.tempOutOfService && phone && phone.includes('SERVPRO') && <h2 css={[styles.nearestHeading, { marginBottom: '0.5rem' }]}>National Call Center</h2>}
                            {phone && (
                                <PhoneNumber
                                    css={styles.phoneNumber}
                                    as={StyledButtonAsLink}
                                    to={`tel:${formatPhoneLink(phone) as string}`}
                                >
                                    {formatPhoneLink(phone)}
                                </PhoneNumber>
                            )}
                        </div>
                    </div>
                </div>
            </NearestWrap>
        );
    }

    return (
        <NearestWrap css={styles.displayOnDesktop}>
            <div css={styles.coreNearestInnerWrap}>
                <div>
                    <h2 css={styles.nearestHeading}>
                        {isLocalCityPage && LOCAL_CITY_LOCATION}
                        {(locationChangedByUser && !isLocalCityPage) && YOUR_SELECTED_LOCATION}
                        {(!locationChangedByUser && !isLocalCityPage) && YOUR_DETECTED_LOCATION}
                    </h2>
                    <div css={styles.nearestLocationWrap}>
                        <div css={styles.nearestLocationHeading}>
                            {isLocalCityPage ? (
                                <>
                                    {microsite?.targetCity?.city || microsite?.franchise?.address?.city}
                                    ,
                                    {' '}
                                    {microsite?.targetCity?.state}
                                </>
                            ) : (
                                <Skeleton css={styles.nearestLocationHeadingSkeleton}
                                >
                                    {locationChangedByUser && name}
                                    {!locationChangedByUser && !!geo && !!geo.city && (
                                        `${geo?.city}${geo?.stateShort ? `, ${geo.stateShort}` : ''
                                        }`
                                    )}
                                </Skeleton>
                            )}
                        </div>
                        {(name && !locationChangedByUser) && (
                            <p css={styles.nearestLocationSubheading}>{name}</p>
                        )}
                        {(!name && !locationChangedByUser) && (
                            <Skeleton css={styles.nearestLocationSubheadingSkeleton} />
                        )}
                        {!isLocalCityPage && (
                            <div
                                className="change-location-open-cta"
                                css={styles.changeLocationCta}
                            >
                                <span>Need help elsewhere?</span>
                                {' '}
                                <StyledPillButton onClick={() => {
                                    setOpen(true);
                                }}
                                >
                                    {CHANGE_LOCATION}
                                </StyledPillButton>
                            </div>
                        )}
                    </div>
                </div>
                <div css={styles.reviewsWrap(true)}>
                    <div css={styles.ratingWrap}
                    >
                        {shouldDisplayReview && (
                            <div css={styles.rating}
                            >
                                <Typography
                                    fontSize={{
                                        dt: 'xl',
                                        mb: 'lg',
                                    }}
                                    css={styles.typography}
                                    fontWeight="medium"
                                    color="primaryOrange"
                                >
                                    24/7 Emergency Service
                                </Typography>
                                <RatingSummary
                                    rating={averageRating}
                                    count={reviewCount}
                                />
                            </div>
                        )}
                    </div>
                    <div css={styles.phoneNumberWrap}
                    >
                        {phone && (
                            <PhoneNumber
                                css={styles.phoneNumber}
                                as={StyledButtonAsLink}
                                to={`tel:${formatPhoneLink(phone) as string}`}
                            >
                                {formatPhoneLink(phone)}
                            </PhoneNumber>
                        )}
                        {!!permalink && !hideFranchiseLink && (
                            <StyledPillButtonAsLink
                                to={permalink}
                                css={styles.findOutMoreLink}
                            >
                                Find Out More ›
                            </StyledPillButtonAsLink>
                        )}
                    </div>
                </div>
            </div>
            <ChangeLocationDrawer
                visible={open}
                onClose={() => setOpen(false)}
            />
        </NearestWrap>
    );
};

const styles = {
    nearestWrap: {
        boxShadow: '2px 2px 10px 3px rgba(0, 0, 0, 0.135359)',
        borderRadius: borderRadius.small,
        backgroundColor: colors.shadesWhite,
        padding: '32px 40px',
        [breakpoints.tb]: {
            padding: '32px',
        },
        [breakpoints.mb]: {
            padding: '24px',
            width: '100%',
            boxShadow: 'none',
            borderBottom: `1px solid ${colors.grey200}`,
            borderTop: `1px solid ${colors.grey200}`,
            borderRadius: '0',
        },
    },
    micrositePhoneWrapper: (shouldDisplayOnDesktop: boolean) => ({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        [breakpoints.mb]: {
            display: 'none',
        },
        // this is the breakpoint where the sticky cta appears
        ['@media (max-width: 1024px)']: {
            display: shouldDisplayOnDesktop ? 'none' : '',
        },
    }),
    reviewsWrap: (shouldDisplayReviewContainer?: boolean) => ({
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        width: '46%',
        justifyContent: shouldDisplayReviewContainer ? 'space-between' : 'flex-end',
        [breakpoints.tb]: {
            width: 'auto',
            flexDirection: 'column',
        },
        [breakpoints.mb]: {
            width: '100%',
        },
        // this is the breakpoint where the sticky CTA appears
        ['@media (max-width: 1024px)']: {
            display: shouldDisplayReviewContainer ? '' : 'none',
        },
    }),
    micrositeNearestInnerWrap: {
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '144px',
        [breakpoints.mb]: {
            minHeight: 'unset',
        },
    },
    typography: {
        lineHeight: '1.2',
    },
    tempOutOfServiceDisclaimer: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        '& a': {
            color: colors.primaryOrange,
            textDecoration: 'underline',
            '&:hover': {
                color: colors.primaryOrangeHover
            }
        }
    },
    changeLocationCta: {
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        [breakpoints.tb]: {
            flexDirection: 'column',
            alignItems: 'flex-start'
        }
    },
    nearestLocationHeadingSkeleton: {
        height: '32px',
        width: '192px',
    },
    nearestLocationSubheadingSkeleton: {
        paddingTop: "0.5rem",
        marginTop: "0.5rem",
        borderRadius: "0.125rem",
        width: "16rem",
        height: "0.75rem"
    },
    nearestLocationWrap: {
        marginBottom: "1.25rem"
    },
    micrositeNearestOuterWrap: (shouldDisplayReviewContainer) => ({
        [breakpoints.mb]: {
            display: shouldDisplayReviewContainer ? 'flex' : 'none',
        },
    }),
    findOutMoreLink: {
        marginRight: '20px',
        marginTop: '10px',
    },
    phoneNumber: {
        minWidth: '256px',
    },
    phoneNumberWrap: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        [breakpoints.mb]: {
            display: 'none',
        },
    },
    ratingWrap: {
        [breakpoints.mb]: {
            display: 'flex',
            gap: '18px',
            justifyContent: 'space-around',
            width: '100%'
        },
        '@media (max-width: 500px)': {
            flexDirection: 'column',
            gap: '4px',
            width: 'unset'
        }
    },
    rating: {
        display: 'flex',
        gap: '4px',
        alignItems: 'flex-start',
        flexDirection: 'column',
    },
    displayOnDesktop: {
        [breakpoints.mb]: {
            display: 'none',
        },
    },
    coreNearestInnerWrap: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "9rem"
    },
    nearestHeading: {
        letterSpacing: "0.08em",
        marginBottom: "1rem",
        color: colors.primaryOrange
    },
    nearestLocationHeading: {
        fontSize: "32px",
        marginBottom: "0.25rem",
        fontWeight: 600,
        lineHeight: "2.25rem",
        "@media (min-width: 1536px)": {
            marginBottom: "0"
        }
    },
    nearestLocationSubheading: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem"
    }
};

export default Nearest;
